import { trpc, queryClient } from '@/utils/trpc';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { SearchProvidersParams } from '../types';

export const useSearchProviders = (params?: SearchProvidersParams) => {
  return useQuery({
    ...trpc.providersRouter.searchProviders.queryOptions(
      params || { providerType: 'individual' },
    ),
    enabled: !!params,
  });
};

export const useSearchProviderLocations = (term: string) => {
  return useQuery({
    ...trpc.providersRouter.searchProviderLocations.queryOptions({
      term,
      pagination: { offset: 0, limit: 100 },
    }),
    enabled: term.length >= 3,
  });
};

/**
 * Infinite query hook for provider search using offset/limit pagination.
 *
 * Note: We use TanStack Query's useInfiniteQuery instead of tRPC's built-in
 * useInfiniteQuery because our searchProviders procedure uses offset/limit
 * pagination rather than cursor-based pagination which tRPC's infinite query expects.
 *
 * @see https://trpc.io/docs/client/react/useInfiniteQuery - tRPC requires cursor input
 */
export const useSearchProvidersInfinite = (
  baseParams?: Omit<SearchProvidersParams, 'pagination'>,
) => {
  return useInfiniteQuery({
    queryKey: ['searchProviders', 'infinite', baseParams],
    queryFn: async ({ pageParam = 0 }) => {
      const params: SearchProvidersParams = {
        ...baseParams!,
        pagination: { offset: pageParam, limit: 5 },
      };

      const queryOptions =
        trpc.providersRouter.searchProviders.queryOptions(params);

      const result = await queryOptions.queryFn?.({
        queryKey: queryOptions.queryKey,
        pageParam,
        direction: 'forward',
        signal: new AbortController().signal,
        meta: undefined,
        client: queryClient,
      });

      return result || { data: [], totalCount: 0 };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const totalLoaded = allPages.reduce(
        (sum, page) => sum + page.data.length,
        0,
      );
      return totalLoaded < lastPage.totalCount ? lastPageParam + 5 : undefined;
    },
    enabled: !!baseParams,
  });
};
